<template>
  <v-container class="upload-sdk-form">
    <div v-if="!uploadSuccess">
      <h1 class="form-title">Upload New SDK Release</h1>
      <v-form @submit.prevent="submitForm" ref="form">
        <v-row>
          <v-col cols="6">
            <v-text-field
              v-model="sdkRelease.version"
              label="Version"
              :rules="[rules.required]"
              required
              variant="outlined"
              color="blue"
            ></v-text-field>
          </v-col>
          <v-col cols="6">
            <v-select
              v-model="sdkRelease.platform"
              :items="platforms"
              label="Platform"
              :rules="[rules.required]"
              required
              variant="outlined"
              color="blue"
            ></v-select>
          </v-col>
        </v-row>

        <v-file-input
          v-model="selectedFile"
          label="SDK File"
          :rules="[rules.fileRequired]"
          required
          variant="outlined"
          color="blue"
          accept=".zip"
          show-size
          counter
          :multiple="false"
        ></v-file-input>


        <div class="release-notes-label">Release Notes</div>
        <TiptapEditor v-model="sdkRelease.release_notes" />

        <v-switch
          v-model="sdkRelease.is_visible"
          label="Is Visible"
          color="blue"
          density="compact"
        ></v-switch>

        <v-switch
          v-model="sdkRelease.should_send_email"
          label="Send Email Notification"
          color="blue"
          density="compact"
        ></v-switch>

        <v-btn type="submit" class="submit-button str-btn-primary" :loading="loading">
          Upload Release
        </v-btn>

        <v-alert
          v-if="errorMessage"
          type="error"
          class="mt-3"
          closable
          variant="tonal"
        >{{ errorMessage }}</v-alert>
      </v-form>
    </div>
    <div v-else class="text-center">
      <v-icon size="64" color="green">mdi-check-circle-outline</v-icon>
      <h1 class="my-4">SDK Release Uploaded Successfully!</h1>
      <v-btn @click="resetForm" class="str-btn-primary">Upload Another SDK</v-btn>
    </div>
  </v-container>
</template>

<script setup>
import { ref, reactive } from 'vue';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-vue';
import { useStore } from 'vuex';
import TiptapEditor from '@/components/TiptapEditor.vue';

const { getAccessTokenSilently } = useAuth0();
const store = useStore();

const sdkRelease = reactive({
  version: '',
  platform: null,
  url: '', // This will be populated after S3 upload
  release_notes: '',
  is_visible: true,
  should_send_email: false,
});

const selectedFile = ref([]); // To store the selected file
const uploadSuccess = ref(false);

const platforms = ['iOS', 'Cross Platform'];

const form = ref(null);
const loading = ref(false);
const errorMessage = ref('');

const rules = {
  required: value => !!value || 'Required.',
  fileRequired: value => (value && value.length > 0) || 'File is required.',
};

const resetForm = () => {
  uploadSuccess.value = false;
  Object.assign(sdkRelease, {
    version: '',
    platform: null,
    url: '',
    release_notes: '',
    is_visible: true,
    should_send_email: false,
  });
  selectedFile.value = [];
  if (form.value) {
    form.value.resetValidation();
  }
};

const submitForm = async () => {
  errorMessage.value = '';
  const { valid } = await form.value.validate();

  if (!valid) {
    errorMessage.value = 'Please correct the form errors.';
    return;
  }

  if (selectedFile.value.length === 0) {
    errorMessage.value = 'Please select a file to upload.';
    return;
  }

  loading.value = true;
  try {
    const token = await getAccessTokenSilently();

    // Step 1: Upload file to S3 via backend endpoint
    const formData = new FormData();
    formData.append('file', selectedFile.value[0]);

    const uploadResponse = await axios.post(
      `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/sdk-upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      }
    );

    sdkRelease.url = uploadResponse.data.s3_url;

    // Step 2: Submit SDK release metadata with the S3 URL
    await axios.post(
      `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/sdk-releases`,
      sdkRelease,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    ).catch(err => {
      // Log the S3 URL of the orphaned file for later cleanup
      console.error(`Failed to create SDK release metadata for S3 object: ${sdkRelease.url}`, err);
      // Re-throw the error to be caught by the outer catch block
      throw err;
    });


    uploadSuccess.value = true;
  } catch (error) {
    console.error('Error during SDK release process:', error);
    errorMessage.value = error.response?.data?.detail || 'Failed to upload SDK release.';
    store.commit('SET_SHOW_ERROR_MODAL', true);
    store.commit('SET_ERROR_MESSAGE', errorMessage.value);
  } finally {
    loading.value = false;
  }
};
</script>

<style lang="scss">
  @import '@/assets/styles/StrVariables.scss'
  @import '@/assets/styles/StrButtons.scss'
  @import '@/assets/styles/UploadSdk.scss'

</style>
